<template>
  <div class="flex flex-col z-20" :class="sidebarStyles">
    <div
      v-if="!isMobileView || state.isVisible"
      class="flex flex-col flex-grow text-left bg-slate-middle text-slate-white overflow-y-scroll overflow-x-hidden"
    >
      <div class="ml-2 mt-2 md:ml-5 md:mt-5 flex flex-col items-start">
        <button
          class="cursor-pointer mt-2 text-xl"
          :class="wallSelectionStyles(WallSelectionType.ALL)"
          @click="setWallSelection(WallSelectionType.ALL)"
        >
          {{ t('allwalls') }}
        </button>
        <button
          class="cursor-pointer mt-2 text-xl"
          :class="wallSelectionStyles(WallSelectionType.SHARED)"
          @click="setWallSelection(WallSelectionType.SHARED)"
        >
          {{ t('sharedwalls') }}
        </button>
        <button
          class="cursor-pointer mt-2 text-xl"
          :class="wallSelectionStyles(WallSelectionType.MINE)"
          @click="setWallSelection(WallSelectionType.MINE)"
        >
          {{ t('mywalls') }}
        </button>
        <button
          class="cursor-pointer mt-2 text-xl"
          :class="wallSelectionStyles(WallSelectionType.TEMPLATES)"
          @click="getTemplateWalls(WallSelectionType.TEMPLATES)"
        >
          {{ t('templates') }}
        </button>
      </div>
      <div class="w-full">
        <button class="ml-2 mt-5 md:ml-5 cursor-pointer text-xl mb-3" :class="wallSelectionStyles()" @click="toggleSubjects">
          {{ t('subjects') }}
        </button>
        <div v-if="state.subjectsAreVisible" class="bg-slate-middle h-full">
          <div class="p-4 w-full flex flex-wrap">
            <div v-for="(subject, index) in subjectsForUserWalls" :key="index" class="cursor-pointer m-px" @click="selectSubject(subject)">
              <div class="flex">
                <span v-if="subject.icon" class="mr-3 h-7 w-7 flex justify-center items-center z-51 bg-slate-dark p-1 rounded-full">{{
                  subject.icon
                }}</span>
                <button
                  class="text-md text-slate-white font-bold bg-slate-dark p-3 -ml-8 mt-2 rounded-xl"
                  :style="`background-color: ${colorStore.actions.selectColor(subject.name).background};`"
                  @click="selectSubject(subject)"
                >
                  <span :style="`color: ${colorStore.actions.selectColor(subject.name).text};`">{{ subject.name }}</span>
                </button>
              </div>
            </div>
            <div v-if="subjectsForUserWalls.length === 0">{{ t('nosubjects') }}</div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isMobileView"
      class="absolute p-5 collapse-btn-border bottom-20 bg-blue-middle flex justify-center items-center h-14 w-11 rounded-r-full cursor-pointer"
      @pointerup.stop="toggleSidebar()"
      @keydown.enter="toggleSidebar()"
      :class="mobileViewWrapperStyles"
    >
      <img class="absolute w-5 h-5 mr-1 invert" :class="mobileViewStyles" :src="Arrow" alt="" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { WallSelectionType } from '@/constants'
  import { Subject } from '@/common/classes'

  import useWallStore from '@/composables/wallStore'

  import useFilterStore from '@/composables/filter'
  import useCommonStore from '@/composables/common'
  import useColorStore from '@/composables/colors'

  import Arrow from '@/assets/icons/fontawesome/angle-left-solid.svg'

  const filter = useFilterStore()
  const common = useCommonStore()
  const wallStore = useWallStore()
  const colorStore = useColorStore()

  const { isMobileView } = common.getters
  const { t } = useI18n()

  const state = reactive({
    isVisible: false,
    subjectsAreVisible: false,
  })

  const mobileViewStyles = computed(() => {
    return state.isVisible ? '' : '-rotate-180'
  })

  const mobileViewWrapperStyles = computed(() => {
    return state.isVisible && isMobileView.value ? 'left-full' : ''
  })

  const sidebarStyles = computed(() => {
    return isMobileView.value ? (state.isVisible ? 'w-1/2 h-full absolute' : '') : 'w-1/6'
  })

  const subjectsForUserWalls = computed(() => {
    let subjects = [] as Subject[]
    for (const wall of wallStore.getters.allWalls.value) {
      for (const subject of wall.details.subjects) {
        if (subjects.findIndex((s) => s.name == subject.name) === -1) subjects.push(subject)
      }
    }
    return subjects
  })

  function wallSelectionStyles(selection?: WallSelectionType) {
    if (!selection && state.subjectsAreVisible) return 'font-extrabold'
    return filter.getters.wallSelection.value === selection ? 'font-extrabold' : 'font-light'
  }

  function selectSubject(subject: Subject) {
    filter.actions.setSubjectFilter(subject)
  }

  function setWallSelection(selection: WallSelectionType) {
    filter.actions.setWallSelection(selection)
    toggleSidebar()
  }

  function getTemplateWalls(selection: WallSelectionType) {
    wallStore.actions.getTemplateWalls().then((res) => {
      if (res.success) setWallSelection(selection)
    })
  }

  function toggleSubjects() {
    state.subjectsAreVisible = !state.subjectsAreVisible
    if (!state.subjectsAreVisible) filter.actions.resetSubjectFilter()
  }

  function toggleSidebar() {
    state.isVisible = !state.isVisible
  }
</script>

<style scoped></style>
